import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AgeSelect } from '../../../../components/inputs/AgeSelect';
import { SingleSelect } from '../../../../components/inputs/SingleSelect';
import {
  DEFAULT_AGE_SELECTION,
  GIFT_AMOUNTS,
  STATE_CODE_TO_FIPS,
  US_STATES,
} from '../../../../config/dropdowns';

import { madlibStyles as styles } from './Madlib.styles';

/**
 * Declare-your-impact section — a fill-in-the-blank sentence with three
 * dropdown selectors and a coral "Discover" CTA that opens the impact map
 * with the chosen filters.
 *
 * Figma reference: node 59:24173 (Madlib instance on landing frame 58:39161).
 */
export function Madlib() {
  const navigate = useNavigate();

  // State + amount start empty (placeholders); age defaults to all under 18.
  // If the amount is left empty, the map falls back to its $5M default.
  const [stateCode, setStateCode] = useState<string>('');
  const [amountId, setAmountId] = useState<string>('');
  const [ageIds, setAgeIds] = useState<string[]>([...DEFAULT_AGE_SELECTION]);

  const stateOptions = useMemo(
    () =>
      [...US_STATES]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((state) => ({ id: state.code, label: state.name })),
    [],
  );

  const handleDiscover = () => {
    const params = new URLSearchParams();
    if (stateCode) {
      params.set('location', STATE_CODE_TO_FIPS[stateCode] ?? stateCode);
    }
    if (amountId) params.set('amount', amountId);
    params.set('age', ageIds.join(','));
    navigate(`/impact?${params.toString()}`);
  };

  return (
    <Box component="section" sx={styles.root} aria-labelledby="madlib-heading">
      <Typography component="h2" id="madlib-heading" sx={styles.heading}>
        Declare your impact
      </Typography>
      <Divider sx={styles.divider} />
      <Typography component="p" sx={styles.prompt}>
        What is your mission statement?
      </Typography>
      <Box sx={styles.sentence}>
        <span>I want to fund children&rsquo;s savings accounts in</span>
        <SingleSelect
          value={stateCode}
          onChange={setStateCode}
          options={stateOptions}
          variant="standard"
          displayEmpty
          placeholder="state"
          ariaLabel="State"
          sx={styles.pill}
        />
        <span>with a gifting donation of</span>
        <SingleSelect
          value={amountId}
          onChange={setAmountId}
          options={GIFT_AMOUNTS}
          variant="standard"
          displayEmpty
          placeholder="amount"
          ariaLabel="Gift amount"
          sx={styles.pill}
        />
        <span>targeting families with children</span>
        <AgeSelect
          value={ageIds}
          onChange={setAgeIds}
          sx={styles.pill}
          ariaLabel="Age ranges"
        />
        <span>.</span>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.actions}>
        <Button variant="contained" color="secondary" onClick={handleDiscover}>
          Discover
        </Button>
      </Box>
    </Box>
  );
}
